"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import { Comment, CommentFormData, CommentCategory } from "@/lib/types";
import CommentForm from "@/components/CommentForm";
import CommentItem from "@/components/CommentItem";
import { getCommentsLikeDataAction, toggleCommentLikeAction } from "@/lib/actions/commentLikes";
import { createComment, editComment, getComments, removeComment } from "@/lib/actions/comments";
import { useCommentDeepLink } from "@/hooks/useCommentDeepLink";
import { toCommentTabs } from "@/lib/comment-category";
import {
  FORUM_COMMENT_CATEGORIES,
  resolveForumCommentCategory,
} from "@/lib/forum-comment-category";

const COMMENTS_PER_PAGE = 10;

// 포럼이 탭으로 노출하는 카테고리(순서 포함) — 딥링크 대상 카테고리 허용 목록이기도 하다 (JCG-3603)
const FORUM_TAB_KEYS = FORUM_COMMENT_CATEGORIES;

// 포럼 전용 3탭 — 이모지·라벨은 comment-category.ts SSOT
const FORUM_TABS = toCommentTabs(FORUM_TAB_KEYS);

// 루트 댓글의 탭 카테고리. 포럼은 잡담을 쓰지 않으므로 chat·null 은 질문 탭으로 본다
// (forum-comment-category.ts SSOT — 탭 필터·집계·딥링크 계획이 같은 함수를 쓴다, JCG-3603)
function resolveForumCategory(comment: Comment): CommentCategory {
  return resolveForumCommentCategory(comment.category);
}

// 모든 댓글 ID 재귀 추출 (컴포넌트 외부 - immutability 규칙 회피)
function getAllCommentIds(comments: Comment[]): string[] {
  return comments.flatMap((comment) => [
    comment.id,
    ...(comment.replies ? getAllCommentIds(comment.replies) : []),
  ]);
}

// 댓글에 좋아요 수 주입 (컴포넌트 외부)
function injectLikeCounts(
  comments: Comment[],
  likeCounts: Record<string, number>
): Comment[] {
  return comments.map((comment) => ({
    ...comment,
    likeCount: likeCounts[comment.id] ?? 0,
    replies: comment.replies
      ? injectLikeCounts(comment.replies, likeCounts)
      : [],
  }));
}

// 댓글이 가장 많은 포럼 탭 찾기 (컴포넌트 외부)
function findMostPopularTab(comments: Comment[]): CommentCategory {
  const counts: Record<CommentCategory, number> = {
    chat: 0,
    suggestion: 0,
    question: 0,
    bug: 0,
  };
  comments.forEach((comment) => {
    counts[resolveForumCategory(comment)]++;
  });

  let maxCount = 0;
  let mostPopularTab: CommentCategory = "question";
  for (const tab of FORUM_TABS) {
    if (counts[tab.key] > maxCount) {
      maxCount = counts[tab.key];
      mostPopularTab = tab.key;
    }
  }
  return mostPopularTab;
}

interface ForumCommentSectionProps {
  problemId: string;
}

export default function ForumCommentSection({ problemId }: ForumCommentSectionProps) {
  const slug = `forum/${problemId}`;
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [likedCommentIds, setLikedCommentIds] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<CommentCategory>("question");
  const [visibleCount, setVisibleCount] = useState(COMMENTS_PER_PAGE);

  const filteredComments = useMemo(() => {
    return comments.filter((comment) => resolveForumCategory(comment) === activeTab);
  }, [comments, activeTab]);

  // 페이지네이션 적용
  const visibleComments = useMemo(() => {
    return filteredComments.slice(0, visibleCount);
  }, [filteredComments, visibleCount]);

  const hasMore = filteredComments.length > visibleCount;

  const commentCounts = useMemo(() => {
    const counts: Record<CommentCategory, number> = {
      chat: 0,
      suggestion: 0,
      question: 0,
      bug: 0,
    };
    comments.forEach((comment) => {
      counts[resolveForumCategory(comment)]++;
    });
    return counts;
  }, [comments]);

  const sortedTabs = useMemo(() => {
    return [...FORUM_TABS].sort((a, b) => commentCounts[b.key] - commentCounts[a.key]);
  }, [commentCounts]);

  // 댓글 딥링크(#comment-{id}, JCG-3603) — CommentSection 과 동일한 계약. loadComments 의
  // setActiveTab(findMostPopularTab) 커밋 뒤에 실행되어 딥링크 탭이 자동 선택 탭을 덮는다.
  const { targetCommentId: deepLinkTargetId, highlightedCommentId } = useCommentDeepLink({
    comments,
    loading,
    pageSize: COMMENTS_PER_PAGE,
    resolveCategory: resolveForumCategory,
    allowedCategories: FORUM_TAB_KEYS,
    activeTab,
    setActiveTab,
    setVisibleCount,
  });

  const loadComments = useCallback(async () => {
    try {
      const result = await getComments(slug);
      if (result.success) {
        const allIds = getAllCommentIds(result.comments);

        if (allIds.length > 0) {
          const likeData = await getCommentsLikeDataAction(allIds);
          if (likeData.success) {
            const commentsWithLikes = injectLikeCounts(
              result.comments,
              likeData.likeCounts
            );
            setComments(commentsWithLikes);
            setLikedCommentIds(new Set(likeData.likedIds));
            setActiveTab(findMostPopularTab(commentsWithLikes));
          } else {
            setComments(result.comments);
            setActiveTab(findMostPopularTab(result.comments));
          }
        } else {
          setComments(result.comments);
          setActiveTab(findMostPopularTab(result.comments));
        }
      } else {
        console.error("댓글 로드 실패:", result.error);
        setComments([]);
      }
    } catch (error) {
      console.error("댓글 로드 실패:", error);
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  // 외부 데이터 fetch — JCG-2034 마이그레이션 예정
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadComments();
  }, [loadComments]);

  const addReplyToComment = (
    comments: Comment[],
    parentId: string,
    newReply: Comment
  ): Comment[] => {
    return comments.map((comment) => {
      if (comment.id === parentId) {
        return { ...comment, replies: [...(comment.replies || []), newReply] };
      } else if (comment.replies && comment.replies.length > 0) {
        return {
          ...comment,
          replies: addReplyToComment(comment.replies, parentId, newReply),
        };
      }
      return comment;
    });
  };

  const replaceReplyInComment = (
    comments: Comment[],
    tempId: string,
    newReply: Comment
  ): Comment[] => {
    return comments.map((comment) => {
      if (comment.id === tempId) {
        return { ...newReply, replies: comment.replies || [] };
      } else if (comment.replies && comment.replies.length > 0) {
        return {
          ...comment,
          replies: replaceReplyInComment(comment.replies, tempId, newReply),
        };
      }
      return comment;
    });
  };

  const removeReplyFromComment = (
    comments: Comment[],
    tempId: string
  ): Comment[] => {
    return comments
      .map((comment) => ({
        ...comment,
        replies: comment.replies
          ? removeReplyFromComment(comment.replies, tempId).filter(
              (reply) => reply.id !== tempId
            )
          : [],
      }))
      .filter((comment) => comment.id !== tempId);
  };

  const updateCommentInComments = (
    comments: Comment[],
    commentId: string,
    updateFn: (comment: Comment) => Comment
  ): Comment[] => {
    return comments.map((comment) => {
      if (comment.id === commentId) {
        return updateFn(comment);
      } else if (comment.replies && comment.replies.length > 0) {
        return {
          ...comment,
          replies: updateCommentInComments(comment.replies, commentId, updateFn),
        };
      }
      return comment;
    });
  };

  const handleAddComment = async (data: CommentFormData) => {
    const commentData: CommentFormData = {
      ...data,
      category: activeTab,
    };

    const tempId = `temp_${Date.now()}`;
    const tempComment: Comment = {
      id: tempId,
      content: commentData.content,
      author: commentData.author,
      email: commentData.email,
      createdAt: new Date().toISOString(),
      replies: [],
      category: activeTab,
    };

    try {
      // 낙관적 업데이트: 최신순이므로 맨 앞에 추가
      setComments((prev) => [tempComment, ...prev]);

      const result = await createComment(slug, commentData);

      if (result.success && result.comment) {
        setComments((prev) =>
          prev.map((comment) =>
            comment.id === tempId
              ? { ...result.comment!, replies: [] }
              : comment
          )
        );
        alert("댓글이 등록되었습니다.");
        return true;
      } else {
        setComments((prev) => prev.filter((comment) => comment.id !== tempId));
        alert(result.error || "댓글 등록에 실패했습니다.");
        return false;
      }
    } catch (error) {
      setComments((prev) => prev.filter((comment) => comment.id !== tempId));
      console.error("댓글 추가 실패:", error);
      alert("댓글 등록에 실패했습니다.");
      return false;
    }
  };

  const handleAddReply = async (parentId: string, data: CommentFormData) => {
    // 현재 활성화된 탭의 카테고리를 데이터에 추가
    const replyData: CommentFormData = {
      ...data,
      category: activeTab,
    };

    const tempId = `temp_${Date.now()}`;
    const tempReply: Comment = {
      id: tempId,
      content: replyData.content,
      author: replyData.author,
      email: replyData.email,
      createdAt: new Date().toISOString(),
      parentId: parentId,
      replies: [],
      category: activeTab,
    };

    try {
      setComments((prev) => addReplyToComment(prev, parentId, tempReply));

      const result = await createComment(slug, replyData, parentId);

      if (result.success && result.comment) {
        setComments((prev) =>
          replaceReplyInComment(prev, tempId, result.comment!)
        );
        alert("답글이 등록되었습니다.");
        return true;
      } else {
        setComments((prev) => removeReplyFromComment(prev, tempId));
        alert(result.error || "답글 등록에 실패했습니다.");
        return false;
      }
    } catch (error) {
      setComments((prev) => removeReplyFromComment(prev, tempId));
      console.error("답글 추가 실패:", error);
      alert("답글 등록에 실패했습니다.");
      return false;
    }
  };

  const handleEditComment = async (
    commentId: string,
    email: string,
    data: CommentFormData
  ) => {
    const originalComments = [...comments];

    try {
      setComments((prev) =>
        updateCommentInComments(prev, commentId, (comment) => ({
          ...comment,
          content: data.content,
          author: data.author,
        }))
      );

      const result = await editComment(commentId, email, data, slug);

      if (result.success && result.comment) {
        // likeCount는 서버 응답에 포함되지 않으므로 기존 값 보존
        setComments((prev) =>
          updateCommentInComments(prev, commentId, (comment) => ({
            ...result.comment!,
            replies: comment.replies || [],
            likeCount: comment.likeCount,
          }))
        );
        alert("댓글이 수정되었습니다.");
        return true;
      } else {
        setComments(originalComments);
        alert(result.error || "댓글 수정에 실패했습니다.");
        return false;
      }
    } catch (error) {
      setComments(originalComments);
      console.error("댓글 수정 실패:", error);
      alert("댓글 수정에 실패했습니다.");
      return false;
    }
  };

  const handleDeleteComment = async (commentId: string, email: string) => {
    const originalComments = [...comments];

    try {
      setComments((prev) =>
        updateCommentInComments(prev, commentId, (comment) => ({
          ...comment,
          author: null,
          content: null,
          isDeleted: true,
        }))
      );

      const result = await removeComment(commentId, email, slug);

      if (result.success) {
        alert("댓글이 삭제되었습니다.");
      } else {
        setComments(originalComments);
        alert(result.error || "댓글 삭제에 실패했습니다.");
      }
    } catch (error) {
      setComments(originalComments);
      console.error("댓글 삭제 실패:", error);
      alert("댓글 삭제에 실패했습니다.");
    }
  };

  const handleLikeComment = async (commentId: string) => {
    const wasLiked = likedCommentIds.has(commentId);

    setLikedCommentIds((prev) => {
      const newSet = new Set(prev);
      if (wasLiked) {
        newSet.delete(commentId);
      } else {
        newSet.add(commentId);
      }
      return newSet;
    });

    setComments((prev) =>
      updateCommentInComments(prev, commentId, (comment) => ({
        ...comment,
        likeCount: (comment.likeCount ?? 0) + (wasLiked ? -1 : 1),
      }))
    );

    try {
      const result = await toggleCommentLikeAction(commentId);

      if (result.success) {
        setLikedCommentIds((prev) => {
          const newSet = new Set(prev);
          if (result.liked) {
            newSet.add(commentId);
          } else {
            newSet.delete(commentId);
          }
          return newSet;
        });

        setComments((prev) =>
          updateCommentInComments(prev, commentId, (comment) => ({
            ...comment,
            likeCount: result.likeCount,
          }))
        );
      } else {
        setLikedCommentIds((prev) => {
          const newSet = new Set(prev);
          if (wasLiked) {
            newSet.add(commentId);
          } else {
            newSet.delete(commentId);
          }
          return newSet;
        });

        setComments((prev) =>
          updateCommentInComments(prev, commentId, (comment) => ({
            ...comment,
            likeCount: (comment.likeCount ?? 0) + (wasLiked ? 1 : -1),
          }))
        );
      }
    } catch (error) {
      console.error("좋아요 처리 실패:", error);
      setLikedCommentIds((prev) => {
        const newSet = new Set(prev);
        if (wasLiked) {
          newSet.add(commentId);
        } else {
          newSet.delete(commentId);
        }
        return newSet;
      });

      setComments((prev) =>
        updateCommentInComments(prev, commentId, (comment) => ({
          ...comment,
          likeCount: (comment.likeCount ?? 0) + (wasLiked ? 1 : -1),
        }))
      );
    }
  };

  if (loading) {
    return (
      <div className="mt-6">
        <div className="rounded-md border-[0.5px] dark:border-neutral-400 p-4">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#706E6E] dark:border-neutral-400"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <div className="rounded-md border-[0.5px] dark:border-neutral-400 pt-8 p-3 sm:p-4">
        <h2 className="border-b-[0.5px] border-gray-200 dark:border-neutral-400 pb-2 text-xl sm:text-2xl font-semibold text-[#333] dark:text-neutral-200 mb-2">
          토론
        </h2>

        {/* 탭 네비게이션 */}
        <div className="flex gap-1 sm:gap-2 mb-4 mt-4 overflow-x-auto">
          {sortedTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setVisibleCount(COMMENTS_PER_PAGE); }}
              className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-4 py-2 rounded-full text-sm sm:text-base font-medium transition-all whitespace-nowrap ${
                activeTab === tab.key
                  ? "bg-[#333] dark:bg-neutral-200 text-white dark:text-neutral-800"
                  : "bg-gray-100 dark:bg-neutral-700 text-[#706E6E] dark:text-neutral-300 hover:bg-gray-200 dark:hover:bg-neutral-600"
              }`}
            >
              <span>{tab.emoji}</span>
              <span className="hidden sm:inline">{tab.label}</span>
              <span
                className={`ml-0.5 sm:ml-1 px-1.5 py-0.5 rounded-full text-xs ${
                  activeTab === tab.key
                    ? "bg-white/20 text-white dark:bg-neutral-800/30 dark:text-neutral-800"
                    : "bg-gray-200 dark:bg-neutral-600 text-[#706E6E] dark:text-neutral-300"
                }`}
              >
                {commentCounts[tab.key]}
              </span>
            </button>
          ))}
        </div>

        {/* 댓글 목록 */}
        <div className="space-y-0">
          {visibleComments.length > 0 ? (
            visibleComments.map((comment, index) => {
              const total = visibleComments.length;
              // 최신순 정렬이므로 가장 최신 2개(index 0, 1)만 truncate 제외
              const shouldTruncate = index >= 2;

              return (
                <div key={comment.id}>
                  <CommentItem
                    comment={comment}
                    onReply={handleAddReply}
                    onEdit={handleEditComment}
                    onDelete={handleDeleteComment}
                    onLike={handleLikeComment}
                    likedCommentIds={likedCommentIds}
                    shouldTruncate={shouldTruncate}
                    deepLinkTargetId={deepLinkTargetId}
                    highlightedCommentId={highlightedCommentId}
                  />
                  {index < total - 1 && (
                    <hr className="border-gray-200 dark:border-neutral-600" />
                  )}
                </div>
              );
            })
          ) : (
            <div className="text-center py-8 text-[#706E6E] dark:text-gray-400 font-light">
              {activeTab === "question" && "아직 질문이 없습니다. 문제에 대한 궁금한 점을 물어보세요!"}
              {activeTab === "bug" && "아직 버그 리포트가 없습니다. 오류를 발견하면 알려주세요!"}
              {activeTab === "suggestion" && "아직 제안이 없습니다. 개선 아이디어를 공유해주세요!"}
            </div>
          )}
        </div>

        {/* 더보기 버튼 */}
        {hasMore && (
          <div className="flex justify-center py-4">
            <button
              onClick={() => setVisibleCount((prev) => prev + COMMENTS_PER_PAGE)}
              className="px-6 py-2 text-sm font-medium text-[#706E6E] dark:text-neutral-300 bg-gray-100 dark:bg-neutral-700 hover:bg-gray-200 dark:hover:bg-neutral-600 rounded-full transition-colors"
            >
              이전 댓글 더보기 ({filteredComments.length - visibleCount}개 남음)
            </button>
          </div>
        )}

        {/* 새 댓글 작성 폼 */}
        <div className="mb-8 p-3 sm:p-4 mt-2 border-[0.5px] border-gray-200 dark:border-neutral-400 bg-[#FAFAFA] dark:bg-gray-800 rounded-md">
          <h3 className="text-base sm:text-lg font-semibold text-[#333] dark:text-neutral-200 mb-4">
            {activeTab === "question" && "❓ 질문하기"}
            {activeTab === "bug" && "🐛 버그 리포트"}
            {activeTab === "suggestion" && "💡 제안하기"}
          </h3>
          <CommentForm onSubmit={handleAddComment} />
        </div>
      </div>
    </div>
  );
}
